import React, { useState, useEffect, useRef } from 'react';

/**
 * Rocket Physics Lab Page
 * Interactive physics simulations and calculators
 */
const PhysicsLab = () => {
  // Simulation state
  const [thrust, setThrust] = useState(5000000); // N
  const [rocketMass, setRocketMass] = useState(500000); // kg
  const [fuelMass, setFuelMass] = useState(200000); // kg
  const [launchAngle, setLaunchAngle] = useState(90); // degrees
  const [isp, setIsp] = useState(300); // seconds (specific impulse)
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [simulationTime, setSimulationTime] = useState(0);
  
  // Physics calculations
  const g = 9.81; // m/s²
  const currentMass = rocketMass + fuelMass;
  const weight = currentMass * g;
  const netForce = thrust - weight;
  const acceleration = netForce > 0 ? netForce / currentMass : 0;
  
  // Graph data
  const [velocityData, setVelocityData] = useState([{ time: 0, velocity: 0 }]);
  const [altitudeData, setAltitudeData] = useState([{ time: 0, altitude: 0 }]);
  const [currentVelocity, setCurrentVelocity] = useState(0);
  const [currentAltitude, setCurrentAltitude] = useState(0);
  
  // Countdown state
  const [countdownTarget, setCountdownTarget] = useState(new Date(Date.now() + 10000)); // 10 seconds
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 10 });
  const [hasLaunched, setHasLaunched] = useState(false);
  
  // Refs for canvas
  const liftOffCanvasRef = useRef(null);
  const trajectoryCanvasRef = useRef(null);
  const velocityGraphRef = useRef(null);
  const altitudeGraphRef = useRef(null);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(countdownTarget).getTime();
      const diff = target - now;

      if (diff <= 0 && !hasLaunched) {
        setHasLaunched(true);
        setIsRunning(true);
      } else if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ days, hours, minutes, seconds });
      }
    }, 100);

    return () => clearInterval(interval);
  }, [countdownTarget, hasLaunched]);

  // Main simulation loop - Completely rewritten for reliability
  useEffect(() => {
    if (!isRunning || isPaused) {
      return;
    }

    let intervalId;
    
    // Use a ref to track if we should continue
    const runSimulation = () => {
      intervalId = setInterval(() => {
        // Get current state values using functional updates
        setFuelMass(prevFuel => {
          if (prevFuel <= 0) {
            setIsRunning(false);
            return 0;
          }
          
          const burnRate = thrust / (isp * g); // kg/s
          const newFuel = Math.max(0, prevFuel - burnRate * 0.1);
          
          // Calculate physics with current fuel
          const currentMassValue = rocketMass + newFuel;
          const currentWeight = currentMassValue * g;
          const currentNetForce = thrust - currentWeight;
          const currentAcceleration = currentNetForce > 0 ? currentNetForce / currentMassValue : 0;
          
          // Update velocity
          setCurrentVelocity(prevVel => {
            const newVel = Math.max(0, prevVel + currentAcceleration * 0.1);
            
            // Update altitude using previous velocity
            setCurrentAltitude(prevAlt => {
              // Only update if we have positive acceleration OR we're already moving
              if (currentAcceleration > 0 || prevVel > 0 || prevAlt > 0) {
                const newAlt = prevAlt + prevVel * 0.1 + 0.5 * currentAcceleration * 0.1 * 0.1;
                const finalAlt = Math.max(0, newAlt);
                
                // Update graph data
                setSimulationTime(prevTime => {
                  const newTime = prevTime + 0.1;
                  setVelocityData(velData => [...velData.slice(-50), { time: newTime, velocity: newVel }]);
                  setAltitudeData(altData => [...altData.slice(-50), { time: newTime, altitude: finalAlt }]);
                  return newTime;
                });
                
                return finalAlt;
              }
              return prevAlt;
            });
            
            return newVel;
          });
          
          return newFuel;
        });
      }, 50); // 50ms = 20 updates per second
    };
    
    runSimulation();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRunning, isPaused, thrust, isp, g, rocketMass]);

  // Use refs to access latest state values in animation
  const currentAltitudeRef = useRef(0);
  const currentVelocityRef = useRef(0);
  const isRunningRef = useRef(false);
  const isPausedRef = useRef(false);
  const hasLaunchedRef = useRef(false);
  const netForceRef = useRef(0);
  const thrustRef = useRef(5000000);
  const weightRef = useRef(0);
  const simulationTimeRef = useRef(0);
  const fuelMassRef = useRef(200000);
  const rocketMassRef = useRef(500000);

  // Update refs when state changes - CRITICAL for animation
  // Combined into fewer effects for better performance
  useEffect(() => {
    currentAltitudeRef.current = currentAltitude;
    currentVelocityRef.current = currentVelocity;
    isRunningRef.current = isRunning;
    isPausedRef.current = isPaused;
    hasLaunchedRef.current = hasLaunched;
    simulationTimeRef.current = simulationTime;
  }, [currentAltitude, currentVelocity, isRunning, isPaused, hasLaunched, simulationTime]);

  useEffect(() => {
    netForceRef.current = netForce;
    thrustRef.current = thrust;
    weightRef.current = weight;
  }, [netForce, thrust, weight]);

  useEffect(() => {
    fuelMassRef.current = fuelMass;
    rocketMassRef.current = rocketMass;
  }, [fuelMass, rocketMass]);

  // Draw lift-off simulation - Enhanced animation with better visuals
  useEffect(() => {
    const canvas = liftOffCanvasRef.current;
    if (!canvas) return;

    const width = 800;
    const height = 500;
    canvas.width = width;
    canvas.height = height;

    let animationFrameId;
    let stars = [];
    
    // Initialize stars for background
    for (let i = 0; i < 100; i++) {
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 2 + 0.5
      });
    }

    const draw = () => {
      // Get latest values from refs
      const alt = currentAltitudeRef.current;
      const vel = currentVelocityRef.current;
      const running = isRunningRef.current;
      const paused = isPausedRef.current;
      const launched = hasLaunchedRef.current;
      const netF = netForceRef.current;
      const thr = thrustRef.current;
      const wgt = weightRef.current;
      const time = simulationTimeRef.current;

      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#000011';
      ctx.fillRect(0, 0, width, height);

      // Draw stars (move down to show rocket ascending)
      ctx.fillStyle = '#ffffff';
      stars.forEach(star => {
        // Move stars down based on altitude to show rocket moving up
        let starY = star.y;
        if (running && launched && !paused) {
          starY = (star.y + alt * 0.1) % height;
        }
        ctx.beginPath();
        ctx.arc(star.x, starY, star.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Ground (moves down as rocket ascends)
      const groundOffset = running && launched && !paused ? Math.min(alt * 0.1, 50) : 0;
      ctx.fillStyle = '#2d5016';
      ctx.fillRect(0, height - 50 + groundOffset, width, 50);
      ctx.strokeStyle = '#1a3309';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, height - 50 + groundOffset, width, 50);

      // Launch pad
      ctx.fillStyle = '#666';
      ctx.fillRect(width / 2 - 50, height - 50 + groundOffset, 100, 20);

      // Rocket position - MUCH MORE VISIBLE MOVEMENT
      const baseY = height - 70; // Base position
      const maxVisibleAltitude = 2000; // Show up to 2000m
      const scaleFactor = 300 / maxVisibleAltitude; // More pixels per meter for visibility
      
      let rocketY = baseY;
      if (running && netF > 0 && launched && !paused && alt > 0) {
        // Rocket moves up - make movement more obvious
        const pixelOffset = Math.min(300, alt * scaleFactor);
        rocketY = baseY - pixelOffset;
      }
      const rocketX = width / 2;

      // Enhanced thrust flame with animation
      if (running && netF > 0 && launched && !paused) {
        const flameIntensity = Math.min(thr / 5000000, 1);
        const flameHeight = 30 + flameIntensity * 60;
        const flameWidth = 15 + Math.sin(time * 10) * 5; // Animated width
        
        // Main flame
        const gradient = ctx.createLinearGradient(rocketX, rocketY + 20, rocketX, rocketY + 20 + flameHeight);
        gradient.addColorStop(0, `rgba(255, 255, 100, ${0.9 * flameIntensity})`);
        gradient.addColorStop(0.3, `rgba(255, 200, 0, ${0.8 * flameIntensity})`);
        gradient.addColorStop(0.6, `rgba(255, 100, 0, ${0.7 * flameIntensity})`);
        gradient.addColorStop(1, `rgba(255, 0, 0, ${0.4 * flameIntensity})`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(rocketX - flameWidth, rocketY + 20);
        ctx.lineTo(rocketX - flameWidth - 5, rocketY + 20 + flameHeight);
        ctx.lineTo(rocketX, rocketY + 30 + flameHeight);
        ctx.lineTo(rocketX + flameWidth + 5, rocketY + 20 + flameHeight);
        ctx.lineTo(rocketX + flameWidth, rocketY + 20);
        ctx.fill();
        
        // Outer glow
        ctx.shadowBlur = 20;
        ctx.shadowColor = 'rgba(255, 200, 0, 0.8)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Rocket body - larger and more visible
      ctx.fillStyle = '#ff6b6b';
      ctx.fillRect(rocketX - 12, rocketY - 50, 24, 50);
      
      // Rocket nose cone
      ctx.beginPath();
      ctx.moveTo(rocketX - 12, rocketY - 50);
      ctx.lineTo(rocketX, rocketY - 75);
      ctx.lineTo(rocketX + 12, rocketY - 50);
      ctx.fill();
      
      // Rocket fins
      ctx.fillStyle = '#cc5555';
      ctx.fillRect(rocketX - 12, rocketY - 10, 8, 15);
      ctx.fillRect(rocketX + 4, rocketY - 10, 8, 15);
      
      // Rocket window
      ctx.fillStyle = '#4a9eff';
      ctx.beginPath();
      ctx.arc(rocketX, rocketY - 30, 5, 0, Math.PI * 2);
      ctx.fill();

      // Altitude indicator line
      if (running && launched && !paused && alt > 0) {
        ctx.strokeStyle = '#0f0';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(rocketX, rocketY - 75);
        ctx.lineTo(rocketX, 0);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Altitude markers
        ctx.fillStyle = '#0f0';
        ctx.font = '12px Arial';
        for (let i = 0; i <= Math.floor(alt / 100); i++) {
          const markerY = baseY - (i * 100 * scaleFactor);
          if (markerY > 0) {
            ctx.fillRect(rocketX - 20, markerY, 40, 1);
            ctx.fillText(`${i * 100}m`, rocketX + 25, markerY + 4);
          }
        }
      }

      // Info panel
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(10, 10, 250, 200);
      
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px Arial';
      ctx.fillText('LAUNCH STATUS', 20, 30);
      
      ctx.font = '12px Arial';
      ctx.fillText(`Thrust: ${(thr / 1000).toFixed(0)} kN`, 20, 50);
      ctx.fillText(`Weight: ${(wgt / 1000).toFixed(0)} kN`, 20, 70);
      ctx.fillStyle = netF > 0 ? '#0f0' : '#f00';
      ctx.fillText(`Net Force: ${(netF / 1000).toFixed(0)} kN`, 20, 90);
      
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px Arial';
      ctx.fillText(`Altitude: ${alt.toFixed(1)} m`, 20, 115);
      ctx.fillText(`Velocity: ${vel.toFixed(1)} m/s`, 20, 135);
      ctx.fillText(`Time: ${time.toFixed(1)} s`, 20, 155);
      
      // Status message with clear warning
      if (!launched) {
        ctx.fillStyle = netF > 0 ? '#0f0' : '#f00';
        ctx.font = 'bold 14px Arial';
        ctx.fillText(netF > 0 ? '✓ Ready for Launch' : '✗ CANNOT LAUNCH!', 20, 210);
        if (netF <= 0) {
          ctx.font = '12px Arial';
          ctx.fillText('Thrust must exceed Weight', 20, 230);
          ctx.fillText(`Need: ${(wgt / 1000 + 1).toFixed(0)} kN minimum`, 20, 250);
        }
      } else if (running && !paused) {
        if (netF > 0) {
          ctx.fillStyle = '#0f0';
          ctx.font = 'bold 18px Arial';
          ctx.fillText('🚀 LAUNCHING!', 20, 210);
        } else {
          ctx.fillStyle = '#f00';
          ctx.font = 'bold 16px Arial';
          ctx.fillText('⚠️ STUCK ON PAD!', 20, 210);
          ctx.font = '12px Arial';
          ctx.fillText('Thrust < Weight', 20, 230);
          ctx.fillText('Increase Thrust to lift off!', 20, 250);
        }
      } else if (paused) {
        ctx.fillStyle = '#ff0';
        ctx.font = 'bold 16px Arial';
        ctx.fillText('⏸ PAUSED', 20, 210);
      }
    };

    // Animation loop
    const animate = () => {
      draw();
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []); // Empty deps - reads from refs continuously

  // Draw velocity graph
  useEffect(() => {
    const canvas = velocityGraphRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = 600;
    const height = canvas.height = 300;

    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Grid
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 10; i++) {
      const y = (height / 10) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Axes
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(50, height - 20);
    ctx.lineTo(50, 20);
    ctx.lineTo(width - 20, height - 20);
    ctx.stroke();

    // Labels
    ctx.fillStyle = '#fff';
    ctx.font = '12px Arial';
    ctx.fillText('Velocity (m/s)', 10, 15);
    ctx.fillText('Time (s)', width - 80, height - 5);

    // Plot data
    if (velocityData.length > 1) {
      ctx.strokeStyle = '#e94560';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      const maxVelocity = Math.max(...velocityData.map(d => d.velocity), 100);
      const maxTime = Math.max(...velocityData.map(d => d.time), 10);
      
      velocityData.forEach((point, i) => {
        const x = 50 + (point.time / maxTime) * (width - 70);
        const y = height - 20 - (point.velocity / maxVelocity) * (height - 40);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.stroke();
    }
  }, [velocityData]);

  // Draw altitude graph
  useEffect(() => {
    const canvas = altitudeGraphRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = 600;
    const height = canvas.height = 300;

    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Grid
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 10; i++) {
      const y = (height / 10) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Axes
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(50, height - 20);
    ctx.lineTo(50, 20);
    ctx.lineTo(width - 20, height - 20);
    ctx.stroke();

    // Labels
    ctx.fillStyle = '#fff';
    ctx.font = '12px Arial';
    ctx.fillText('Altitude (m)', 10, 15);
    ctx.fillText('Time (s)', width - 80, height - 5);

    // Plot data
    if (altitudeData.length > 1) {
      ctx.strokeStyle = '#4a9eff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      const maxAltitude = Math.max(...altitudeData.map(d => d.altitude), 100);
      const maxTime = Math.max(...altitudeData.map(d => d.time), 10);
      
      altitudeData.forEach((point, i) => {
        const x = 50 + (point.time / maxTime) * (width - 70);
        const y = height - 20 - (point.altitude / maxAltitude) * (height - 40);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.stroke();
    }
  }, [altitudeData]);

  // Draw trajectory projection - Fixed and enhanced
  useEffect(() => {
    const canvas = trajectoryCanvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const ctx = canvas.getContext('2d');
      const width = 800;
      const height = 500;
      canvas.width = width;
      canvas.height = height;

      // Clear canvas
      ctx.fillStyle = '#000011';
      ctx.fillRect(0, 0, width, height);

      // Stars background
      ctx.fillStyle = '#ffffff';
      for (let i = 0; i < 100; i++) {
        const x = (i * 37) % width;
        const y = (i * 73) % height;
        ctx.fillRect(x, y, 1, 1);
      }

      // Ground
      const groundY = height - 40;
      ctx.fillStyle = '#2d5016';
      ctx.fillRect(0, groundY, width, 40);
      ctx.strokeStyle = '#1a3309';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, groundY, width, 40);

      // Calculate trajectory
      const angleRad = (launchAngle * Math.PI) / 180;
      const v0 = 150; // Initial velocity in m/s
      
      // Handle special case: 90 degrees (straight up)
      if (launchAngle === 90) {
        const maxHeight = (v0 * v0) / (2 * g);
        const scaleY = (groundY - 50) / maxHeight;
        
        // Draw vertical trajectory
        ctx.strokeStyle = '#e94560';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(width / 2, groundY);
        ctx.lineTo(width / 2, groundY - maxHeight * scaleY);
        ctx.stroke();
        
        // Rocket at launch point
        ctx.fillStyle = '#ff6b6b';
        ctx.fillRect(width / 2 - 8, groundY - 20, 16, 20);
        ctx.beginPath();
        ctx.moveTo(width / 2 - 8, groundY - 20);
        ctx.lineTo(width / 2, groundY - 40);
        ctx.lineTo(width / 2 + 8, groundY - 20);
        ctx.fill();
        
        // Height label
        ctx.fillStyle = '#fff';
        ctx.font = '14px Arial';
        ctx.fillText(`Max Height: ${maxHeight.toFixed(0)}m`, width / 2 + 20, groundY - maxHeight * scaleY / 2);
      } else if (launchAngle === 0) {
        // Horizontal launch (0 degrees)
        ctx.strokeStyle = '#e94560';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(50, groundY);
        ctx.lineTo(width - 50, groundY);
        ctx.stroke();
        
        // Rocket at launch point
        ctx.fillStyle = '#ff6b6b';
        ctx.fillRect(50 - 8, groundY - 20, 16, 20);
        ctx.beginPath();
        ctx.moveTo(50 - 8, groundY - 20);
        ctx.lineTo(50, groundY - 40);
        ctx.lineTo(50 + 8, groundY - 20);
        ctx.fill();
        
        ctx.fillStyle = '#fff';
        ctx.font = '12px Arial';
        ctx.fillText('Horizontal trajectory (no height)', 70, groundY - 30);
      } else if (launchAngle > 0 && launchAngle < 90) {
        // Calculate trajectory for angles between 0 and 90
        const maxRange = (v0 * v0 * Math.sin(2 * angleRad)) / g;
        const maxHeight = (v0 * v0 * Math.sin(angleRad) * Math.sin(angleRad)) / (2 * g);
        
        // Scaling factors - ensure we can see the trajectory
        const padding = 50;
        const availableWidth = width - 2 * padding;
        const availableHeight = groundY - padding;
        
        // Use reasonable scaling that ensures visibility
        const scaleX = availableWidth / Math.max(maxRange || 100, 100);
        const scaleY = availableHeight / Math.max(maxHeight * 1.5 || 100, 100);
        
        // Draw trajectory curve using time-based calculation
        ctx.strokeStyle = '#e94560';
        ctx.lineWidth = 3;
        ctx.beginPath();
        
        let firstPoint = true;
        const timeStep = 0.05; // Smaller step for smoother curve
        let t = 0;
        let lastY = 0;
        
        while (t < 30 && lastY >= -10) { // Extended time limit
          const x = v0 * Math.cos(angleRad) * t;
          const y = v0 * Math.sin(angleRad) * t - 0.5 * g * t * t;
          lastY = y;
          
          if (y >= -1) { // Allow slight negative for ground impact
            const screenX = padding + x * scaleX;
            const screenY = groundY - y * scaleY;
            
            if (screenX >= padding - 10 && screenX <= width - padding + 10 && screenY >= padding - 10 && screenY <= groundY + 10) {
              if (firstPoint) {
                ctx.moveTo(screenX, screenY);
                firstPoint = false;
              } else {
                ctx.lineTo(screenX, screenY);
              }
            }
            
            // Stop if we've gone off screen horizontally
            if (screenX > width - padding + 10) {
              break;
            }
          } else {
            // Draw impact point
            const impactX = padding + x * scaleX;
            if (impactX <= width - padding) {
              ctx.lineTo(impactX, groundY);
            }
            break; // Hit ground
          }
          
          t += timeStep;
        }
        ctx.stroke();
        
        // Rocket at launch point
        const launchX = padding;
        const launchY = groundY;
        ctx.fillStyle = '#ff6b6b';
        ctx.fillRect(launchX - 8, launchY - 20, 16, 20);
        ctx.beginPath();
        ctx.moveTo(launchX - 8, launchY - 20);
        ctx.lineTo(launchX, launchY - 40);
        ctx.lineTo(launchX + 8, launchY - 20);
        ctx.fill();
        
        // Launch angle indicator
        ctx.strokeStyle = '#888';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(launchX, launchY);
        const indicatorLength = 50;
        ctx.lineTo(
          launchX + Math.cos(angleRad) * indicatorLength,
          launchY - Math.sin(angleRad) * indicatorLength
        );
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Labels
        ctx.fillStyle = '#fff';
        ctx.font = '12px Arial';
        ctx.fillText(`Range: ${maxRange.toFixed(0)}m`, width - 150, groundY - 10);
        ctx.fillText(`Height: ${maxHeight.toFixed(0)}m`, width - 150, groundY - 30);
      }
      
      // Angle and velocity display
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px Arial';
      ctx.fillText(`Launch Angle: ${launchAngle}°`, 20, 30);
      ctx.fillText(`Initial Velocity: ${v0} m/s`, 20, 50);
    };

    draw();
  }, [launchAngle, g]);

  // Rocket equation calculator state
  const [initialMass, setInitialMass] = useState(1000);
  const [finalMass, setFinalMass] = useState(500);
  const [exhaustVelocity, setExhaustVelocity] = useState(3000);

  // Rocket equation calculator
  const calculateDeltaV = () => {
    if (initialMass <= 0 || finalMass <= 0 || exhaustVelocity <= 0) return 0;
    if (finalMass >= initialMass) return 0;
    
    return exhaustVelocity * Math.log(initialMass / finalMass);
  };

  const deltaV = calculateDeltaV();
  const massRatio = initialMass > 0 && finalMass > 0 ? initialMass / finalMass : 0;

  // Fuel percentage
  const fuelPercentage = (fuelMass / (rocketMass + fuelMass)) * 100;

  // Reset simulation
  const handleReset = () => {
    setIsRunning(false);
    setIsPaused(false);
    setSimulationTime(0);
    setCurrentVelocity(0);
    setCurrentAltitude(0);
    setVelocityData([{ time: 0, velocity: 0 }]);
    setAltitudeData([{ time: 0, altitude: 0 }]);
    setFuelMass(200000);
    setHasLaunched(false);
    setCountdownTarget(new Date(Date.now() + 10000));
  };

  const handleStart = () => {
    // Check if thrust is sufficient before starting
    if (netForce <= 0) {
      alert(`⚠️ Cannot Launch!\n\nThrust (${(thrust / 1000).toFixed(0)} kN) must exceed Weight (${(weight / 1000).toFixed(0)} kN)\n\nPlease increase Thrust to at least ${((weight / 1000) + 1).toFixed(0)} kN`);
      return;
    }
    
    if (!hasLaunched) {
      setHasLaunched(true);
      setIsRunning(true);
    } else {
      setIsRunning(true);
      setIsPaused(false);
    }
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  return (
    <div className="container" style={{ paddingTop: '30px' }}>
      <h1 style={{ fontSize: '36px', marginBottom: '10px', textAlign: 'center' }}>
        🧪 Rocket Physics Lab
      </h1>
      <p style={{ textAlign: 'center', color: 'var(--text-dark)', marginBottom: '30px' }}>
        Interactive physics simulations and calculations for rocket science education
      </p>

      {/* Countdown Timer Section */}
      <section className="card" style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
          ⏱️ Launch Countdown
        </h2>
        <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Countdown Timer Formula</h3>
          <p style={{ color: 'var(--text-dark)', marginBottom: '10px', lineHeight: '1.6' }}>
            <strong>Formula:</strong> remainingTime = launchTime - currentTime
          </p>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
            Where:
          </p>
          <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
            <li><strong>launchTime</strong> = Target launch time (10 seconds from start)</li>
            <li><strong>currentTime</strong> = Current system time</li>
            <li><strong>remainingTime</strong> = Time difference in milliseconds</li>
          </ul>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
            <strong>Explanation:</strong> The countdown calculates the time remaining until launch. 
            When it reaches zero (T-0), the simulation automatically starts. This mimics real rocket launches 
            where precise timing is critical for mission success.
          </p>
        </div>
        {!hasLaunched ? (
          <div className="countdown">
            <div className="countdown-item">
              <span className="countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
              <span className="countdown-label">Seconds</span>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', fontSize: '24px', color: 'var(--highlight-color)' }}>
            🚀 LAUNCHED!
          </div>
        )}
      </section>

      {/* Controls Panel */}
      <section className="card" style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
          🎛️ Controls
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Thrust: {(thrust / 1000).toFixed(0)} kN
            </label>
            <input
              type="range"
              min="0"
              max="10000000"
              step="100000"
              value={thrust}
              onChange={(e) => setThrust(parseFloat(e.target.value))}
              disabled={isRunning}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Rocket Mass: {(rocketMass / 1000).toFixed(0)} tons
            </label>
            <input
              type="range"
              min="100000"
              max="1000000"
              step="10000"
              value={rocketMass}
              onChange={(e) => setRocketMass(parseFloat(e.target.value))}
              disabled={isRunning}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Fuel Mass: {(fuelMass / 1000).toFixed(0)} tons
            </label>
            <input
              type="range"
              min="0"
              max="500000"
              step="10000"
              value={fuelMass}
              onChange={(e) => setFuelMass(parseFloat(e.target.value))}
              disabled={isRunning}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Launch Angle: {launchAngle}°
            </label>
            <input
              type="range"
              min="0"
              max="90"
              step="1"
              value={launchAngle}
              onChange={(e) => setLaunchAngle(parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Specific Impulse (Isp): {isp} s
            </label>
            <input
              type="range"
              min="100"
              max="500"
              step="10"
              value={isp}
              onChange={(e) => setIsp(parseFloat(e.target.value))}
              disabled={isRunning}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={handleStart} className="btn btn-primary" disabled={isRunning && !isPaused}>
            {hasLaunched ? (isPaused ? 'Resume' : 'Start') : 'Start'}
          </button>
          <button onClick={handlePause} className="btn btn-secondary" disabled={!isRunning}>
            {isPaused ? 'Resume' : 'Pause'}
          </button>
          <button onClick={handleReset} className="btn btn-secondary">
            Reset
          </button>
        </div>

        {/* Fuel Bar */}
        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px' }}>Fuel Level: {fuelPercentage.toFixed(1)}%</label>
          <div style={{ width: '100%', height: '30px', background: 'var(--card-hover)', borderRadius: '5px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${fuelPercentage}%`,
                height: '100%',
                background: fuelPercentage > 50 ? '#0f0' : fuelPercentage > 25 ? '#ff0' : '#f00',
                transition: 'width 0.1s ease'
              }}
            />
          </div>
          <div style={{ marginTop: '15px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Fuel Burn Rate</h3>
            <p style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>Formula:</strong> ṁ = T / (Isp × g)
            </p>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              Where:
            </p>
            <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
              <li><strong>ṁ</strong> = Mass flow rate (kg/s) - Rate at which fuel is consumed</li>
              <li><strong>T</strong> = Thrust (N)</li>
              <li><strong>Isp</strong> = Specific impulse (s) - Efficiency of the rocket engine</li>
              <li><strong>g</strong> = Standard gravity = 9.81 m/s²</li>
            </ul>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Current Calculation:</strong>
            </p>
            <p style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
              ṁ = {thrust.toFixed(0)} / ({isp} × 9.81) = {(thrust / (isp * g)).toFixed(2)} kg/s
            </p>
            <p style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Remaining Fuel:</strong> {fuelMass.toFixed(0)} kg ({fuelPercentage.toFixed(1)}%)
            </p>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Explanation:</strong> Higher thrust requires more fuel consumption. Higher Isp (specific impulse) 
              means the engine is more efficient and uses less fuel for the same thrust. The simulation stops when fuel reaches zero.
            </p>
          </div>
        </div>
      </section>

      {/* Lift-Off Simulation */}
      <section className="card" style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
          🚀 Lift-Off Simulation
        </h2>
        <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Physics Principle</h3>
          <p style={{ color: 'var(--text-dark)', marginBottom: '10px', lineHeight: '1.6' }}>
            <strong>Condition for Lift-Off:</strong> T {'>'} mg
          </p>
          <p style={{ color: 'var(--text-dark)', marginBottom: '10px', lineHeight: '1.6' }}>
            Where:
          </p>
          <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>T</strong> = Thrust force (N) - The upward force produced by the rocket engine</li>
            <li><strong>m</strong> = Total mass (kg) - Rocket mass + Fuel mass</li>
            <li><strong>g</strong> = Gravitational acceleration = 9.81 m/s²</li>
            <li><strong>mg</strong> = Weight force (N) - The downward force due to gravity</li>
          </ul>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Explanation:</strong> For a rocket to lift off, the thrust must be greater than its weight. 
            The net force (T - mg) determines the acceleration. If net force is positive, the rocket accelerates upward. 
            If net force is zero or negative, the rocket cannot lift off.
          </p>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Net Force:</strong> F_net = T - mg = {netForce.toFixed(0)} N
          </p>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Acceleration:</strong> a = F_net / m = {acceleration.toFixed(2)} m/s²
          </p>
        </div>
        <canvas 
          ref={liftOffCanvasRef} 
          style={{ 
            border: '2px solid var(--accent-color)', 
            borderRadius: '8px', 
            display: 'block', 
            margin: '0 auto',
            width: '800px',
            height: '500px',
            maxWidth: '100%'
          }} 
        />
      </section>

      {/* Graphs Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {/* Velocity Graph */}
        <section className="card">
          <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
            📈 Velocity vs Time
          </h2>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Formulas</h3>
            <p style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>1. Acceleration:</strong> a = (T - mg) / m
            </p>
            <p style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>2. Velocity:</strong> v = v₀ + at
            </p>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              Where:
            </p>
            <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
              <li><strong>a</strong> = Acceleration (m/s²)</li>
              <li><strong>v</strong> = Current velocity (m/s)</li>
              <li><strong>v₀</strong> = Initial velocity = 0 m/s (starts from rest)</li>
              <li><strong>t</strong> = Time elapsed (s)</li>
            </ul>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Current Calculation:</strong>
            </p>
            <p style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
              a = ({thrust.toFixed(0)} - {weight.toFixed(0)}) / {currentMass.toFixed(0)} = {acceleration.toFixed(2)} m/s²
            </p>
            <p style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
              v = 0 + {acceleration.toFixed(2)} × {simulationTime.toFixed(1)} = <strong>{currentVelocity.toFixed(2)} m/s</strong>
            </p>
          </div>
          <canvas ref={velocityGraphRef} style={{ border: '2px solid var(--accent-color)', borderRadius: '8px', display: 'block' }} />
        </section>

        {/* Altitude Graph */}
        <section className="card">
          <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
            📈 Altitude vs Time
          </h2>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Formula</h3>
            <p style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>Altitude:</strong> h = v₀t + ½at²
            </p>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              Where:
            </p>
            <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
              <li><strong>h</strong> = Altitude/Height (m)</li>
              <li><strong>v₀</strong> = Initial velocity = 0 m/s</li>
              <li><strong>t</strong> = Time elapsed (s)</li>
              <li><strong>a</strong> = Acceleration (m/s²)</li>
            </ul>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Explanation:</strong> This is the kinematic equation for position under constant acceleration. 
              Since the rocket starts from rest (v₀ = 0), the formula simplifies to h = ½at². 
              The altitude increases quadratically with time when acceleration is constant.
            </p>
            <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Current Calculation:</strong>
            </p>
            <p style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
              h = 0 × {simulationTime.toFixed(1)} + ½ × {acceleration.toFixed(2)} × ({simulationTime.toFixed(1)})²
            </p>
            <p style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
              h = <strong>{currentAltitude.toFixed(2)} m</strong>
            </p>
          </div>
          <canvas ref={altitudeGraphRef} style={{ border: '2px solid var(--accent-color)', borderRadius: '8px', display: 'block' }} />
        </section>
      </div>

      {/* Trajectory Projection */}
      <section className="card" style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
          🎯 Trajectory Projection
        </h2>
        <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Projectile Motion Formula</h3>
          <p style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
            <strong>Trajectory Equation:</strong> y = x tan(θ) − (g x²) / (2 v² cos² θ)
          </p>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
            Where:
          </p>
          <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
            <li><strong>y</strong> = Vertical position (m)</li>
            <li><strong>x</strong> = Horizontal position (m)</li>
            <li><strong>θ</strong> = Launch angle (degrees) - Currently: {launchAngle}°</li>
            <li><strong>v</strong> = Initial velocity (m/s) - Set to 100 m/s for visualization</li>
            <li><strong>g</strong> = Gravitational acceleration = 9.81 m/s²</li>
          </ul>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
            <strong>Key Points:</strong>
          </p>
          <ul style={{ color: 'var(--text-dark)', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
            <li><strong>Maximum Range:</strong> R = (v² sin(2θ)) / g</li>
            <li><strong>Maximum Height:</strong> H = (v² sin²(θ)) / (2g)</li>
            <li>45° angle gives maximum range for a given velocity</li>
            <li>90° angle (straight up) gives maximum height but zero range</li>
          </ul>
          <p style={{ color: 'var(--text-dark)', marginTop: '10px', fontSize: '13px', lineHeight: '1.6' }}>
            <strong>Explanation:</strong> This equation describes the parabolic path of a projectile under gravity. 
            The trajectory depends on launch angle and initial velocity. Adjust the angle slider to see how it affects the path.
          </p>
        </div>
        <canvas 
          ref={trajectoryCanvasRef} 
          style={{ 
            border: '2px solid var(--accent-color)', 
            borderRadius: '8px', 
            display: 'block', 
            margin: '0 auto',
            width: '800px',
            height: '500px',
            maxWidth: '100%'
          }} 
        />
      </section>

      {/* Rocket Equation Calculator */}
      <section className="card" style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
          🧮 Rocket Equation Calculator
        </h2>
        <p style={{ color: 'var(--text-dark)', marginBottom: '15px' }}>
          <strong>Tsiolkovsky Rocket Equation:</strong> Δv = vₑ ln(m₀ / m_f)
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div className="form-group">
            <label htmlFor="initialMass">Initial Mass (kg)</label>
            <input
              type="number"
              id="initialMass"
              value={initialMass}
              onChange={(e) => setInitialMass(parseFloat(e.target.value) || 1000)}
              style={{ width: '100%', padding: '10px' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="finalMass">Final Mass (kg)</label>
            <input
              type="number"
              id="finalMass"
              value={finalMass}
              onChange={(e) => setFinalMass(parseFloat(e.target.value) || 500)}
              style={{ width: '100%', padding: '10px' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="exhaustVelocity">Exhaust Velocity (m/s)</label>
            <input
              type="number"
              id="exhaustVelocity"
              value={exhaustVelocity}
              onChange={(e) => setExhaustVelocity(parseFloat(e.target.value) || 3000)}
              style={{ width: '100%', padding: '10px' }}
            />
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '20px', background: 'var(--card-hover)', borderRadius: '8px' }}>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>Δv (Delta-V)</p>
          <p style={{ fontSize: '36px', color: 'var(--highlight-color)', fontWeight: 'bold' }}>
            {deltaV.toFixed(2)} m/s
          </p>
        </div>
        <div style={{ marginTop: '20px', padding: '15px', background: 'var(--card-hover)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-dark)' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--highlight-color)' }}>Detailed Explanation</h3>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            <strong>The Tsiolkovsky Rocket Equation:</strong> Δv = vₑ ln(m₀ / m_f)
          </p>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            Where:
          </p>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Δv</strong> = Change in velocity (m/s) - The total velocity change the rocket can achieve</li>
            <li><strong>vₑ</strong> = Exhaust velocity (m/s) - Speed at which propellant is ejected</li>
            <li><strong>m₀</strong> = Initial mass (kg) - Total mass including fuel</li>
            <li><strong>m_f</strong> = Final mass (kg) - Mass after fuel is expended</li>
            <li><strong>ln</strong> = Natural logarithm</li>
          </ul>
          <p style={{ marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Key Concepts:</strong>
          </p>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Mass Ratio:</strong> m₀/m_f - Higher ratio means more fuel, more Δv</li>
            <li><strong>Exhaust Velocity:</strong> Higher vₑ means more efficient propulsion</li>
            <li><strong>Logarithmic Relationship:</strong> Doubling fuel doesn't double Δv - it's logarithmic</li>
            <li><strong>Practical Limits:</strong> Real rockets have mass ratios of 10-20, limiting Δv</li>
          </ul>
          <p style={{ marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Real-World Applications:</strong>
          </p>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li>Earth orbit: ~9,400 m/s Δv needed</li>
            <li>Moon landing: ~15,000 m/s Δv needed</li>
            <li>Mars mission: ~18,000 m/s Δv needed</li>
            <li>This is why multi-stage rockets are used - each stage can achieve more Δv</li>
          </ul>
          <p style={{ marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Current Calculation Breakdown:</strong>
          </p>
          <p style={{ lineHeight: '1.6' }}>
            Mass Ratio = m₀/m_f = {initialMass} / {finalMass} = {massRatio.toFixed(2)}
          </p>
          <p style={{ lineHeight: '1.6' }}>
            Δv = {exhaustVelocity} × ln({massRatio.toFixed(2)}) = {exhaustVelocity} × {Math.log(massRatio).toFixed(2)} = <strong>{deltaV.toFixed(2)} m/s</strong>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PhysicsLab;
